import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Formik, useFormik } from "formik";
import { DateTime } from "luxon";

import { faCloudArrowDown, faFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlusIcon } from "@heroicons/react/20/solid";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { InputDropDownSpan } from "@/shared/components/atoms/InputDropDownSpan";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { BillsFilter } from "@/shared/components/molecules/BillsFilter";
import { BillsTable } from "@/shared/components/organisms/BillsTable";
import { subcategoriesOptions } from "@/app/accounting/accounting";
import { useMutationToast } from "@/shared/hooks/useMutationToast";
import { Select } from "@/shared/components/organisms/Select";
import { currencies } from "@/app/fees/fees";

import {
  billInitialValues,
  billValidationSchema,
} from "@/shared/schemas/accounting.schema";

import { useCurrenciesQuery } from "@/app/accounting/accounting.api";
import { BASE_URL, DOMAIN } from "@/app/app.api";
import {
  useBillsQuery,
  useCreateBillMutation,
  useDeleteBillMutation,
  useLazyExportBillsQuery,
  useUpdateBillMutation,
} from "@/app/bills/bills.api";
import {
  useBranchsGymQuery,
  usePersonalQuery,
} from "@/app/branchs/branchs.api";
import { useDebounce } from "@/shared/hooks/debounce";
import { toasts } from "@/shared/utils/toasts";
import { InputFileXML } from "@/shared/components/atoms/InputFileXML";

interface BillsPageProps {
  id: number;
  date: string;
  time: string;
  subject: string;
  description: string;
  amount: number;
  currency: string;
  selledBy: string;
  approvedBy: string;
  subcategory: undefined;
  invoice: string;
  xml: string;
}

export const BillsPage: FC<any> = () => {
  const { id } = useParams();
  const [t] = useTranslation();
  const [filterVisible, setFilterVisible] = useState(false);

  const [page, setPage] = useState(1);

  const [billFormVisible, setBillFormVisible] = useState(false);
  const [currentBillEdit, setCurrentBillEdit] = useState<
    BillsPageProps | undefined
  >();

  const filterFormik = useFormik({
    initialValues: {
      currency: "EUR",
      startDate: DateTime.now().toFormat("yyyy-MM-dd"),
      endDate: DateTime.now().toFormat("yyyy-MM-dd"),
      maxAmount: 999999,
      minAmount: 0,
      branchId: id,
      approvedByIds: [],
      createdByIds: [],
      subject: "",
      subcategories: [],
    },
    onSubmit: () => {},
  });

  const debounceValuesForm = useDebounce(filterFormik.values, 500);

  const branchsQuery = useBranchsGymQuery({ branchId: id });
  const personalQuery = usePersonalQuery({ branchId: id, page });
  const currenciesQuery = useCurrenciesQuery({
    branchId: id,
  });

  const {
    data: bills,
    isLoading,
    isFetching,
    refetch,
  } = useBillsQuery({
    subject: debounceValuesForm.subject,
    minAmount: debounceValuesForm.minAmount,
    maxAmount: debounceValuesForm.maxAmount,
    branchId: debounceValuesForm.branchId,
    startDate: debounceValuesForm.startDate,
    endDate: debounceValuesForm.endDate,
    currency:
      typeof debounceValuesForm.currency === "string"
        ? debounceValuesForm.currency
        : debounceValuesForm.currency.name,
    // Fixed array parameters with debounced values
    createdByIds: debounceValuesForm.createdByIds || [],
    approvedByIds: debounceValuesForm.approvedByIds || [],
    subcategories: debounceValuesForm.subcategories || [],
  });
  useEffect(() => {
    if (filterFormik.values) {
      if (bills) {
        refetch();
      }
    }
  }, [filterFormik.values]);

  const branchsOptions = useMemo(
    () =>
      branchsQuery?.data?.map((branch) => ({
        id: branch,
        name: branch,
        value: branch,
      })) ?? [],
    [branchsQuery]
  );

  const [exportBills, exportBillsResult] = useLazyExportBillsQuery();
  const [createBill, createBillResult] = useCreateBillMutation();
  const [updateBill, updateBillResult] = useUpdateBillMutation();
  const [deleteBill, deleteBillResult] = useDeleteBillMutation();

  useMutationToast(
    "Gasto creado con éxito",
    "Ocurrió un error al crear el gasto",
    createBillResult
  );
  useMutationToast(
    "Gasto actualizado con éxito",
    "Ocurrió un error al actualizar el gasto",
    updateBillResult
  );
  useMutationToast(
    "Gasto eliminado con éxito",
    "Ocurrió un error al eliminar el gasto",
    deleteBillResult
  );

  const currencyOptions = useMemo(
    () =>
      currenciesQuery?.data?.map((currency, index) => ({
        id: index + 1,
        name: currency,
        value: currency,
      })) ?? [],
    [currenciesQuery]
  );

  const personalOptions = useMemo(
    () =>
      personalQuery?.data?.data?.data?.map((personal) => ({
        id: personal.id,
        name: personal.name,
      })) ?? [],
    [personalQuery]
  );

  const handleExportBills = () => {
    exportBills({
      ...filterFormik.values,
      branchId: id,
    });
  };

  //USE EFFECT TO FETCH DATA CUANDO SE CARGA EL COMPONENTE
  const handleOpenEdit = async (id: string | number | null) => {
    const billEdit = bills.find((bill) => bill.id === id);
    if (billEdit) {
      setCurrentBillEdit(billEdit);
      setBillFormVisible(true);
    } else {
      console.error("No se encontró la compañía con ID:", id);
    }
  };

  const handleClose = () => {
    setCurrentBillEdit(undefined);
  };

  return (
    <Fragment>
      <div className="flex flex-col flex-1 h-full w-full overflow-y-scroll pt-8">
        <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
          <div className="flex-1 px-4 sm:px-6 lg:px-8">
            <div className="flex sm:flex sm:items-center">
              <div className="flex-grow">
                <h1 className="text-base font-semibold leading-6 text-gray-900">
                  {t("pages.bills.title")}
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  {t("pages.bills.subtitle")}
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0"></div>
            </div>

            {/* Divider debajo del texto */}
            <div className="border-b border-gray-300 my-3"></div>

            <div className="sm:flex sm:items-center">
              <div className="sm:flex w-full sm:max-w-xs">
                <InputWithCornerHint
                  label={t("general.search")}
                  span=""
                  id=""
                  name="subject"
                  type=""
                  className="w-full"
                  placeholder={t("general.search")}
                  error={false}
                  onChange={filterFormik.handleChange}
                  value={filterFormik.values.subject}
                />
                <button
                  type="button"
                  className="sm:ml-2 mt-5 p-2 text-gray-400 hover:text-gray-500"
                  onClick={() => setFilterVisible(!filterVisible)}
                >
                  <span className="sr-only">View KPIs</span>
                  <FontAwesomeIcon
                    icon={faFilter}
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </button>
              </div>
              <div className="mt-8 sm:ml-auto sm:mt-4 flex content-center items-center sm:space-x-4">
                <button
                  type="button"
                  onClick={handleExportBills}
                  className="p-2 text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">View KPIs</span>
                  <FontAwesomeIcon
                    icon={faCloudArrowDown}
                    aria-hidden="true"
                    className="h-8 w-8"
                  />
                </button>

                <button
                  onClick={() => setBillFormVisible(true)}
                  type="button"
                  className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                >
                  <PlusIcon aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="border-b border-gray-300 mt-5"></div>
            <div
              className="flex flex-col w-full py-8 gap-4"
              style={{ height: 500 }}
            >
              <BillsTable
                isLoading={isLoading || isFetching}
                bills={bills ?? []}
                onCreate={() => setBillFormVisible(true)}
                onEdit={handleOpenEdit}
                onDelete={(bill) =>
                  deleteBill({
                    branchId: id,
                    id: bill.id,
                  })
                }
                onDownloadInvoice={(bill) => {
                  if (!bill.invoiceUrl) {
                    toasts.warning(
                      "Oops!",
                      "No contamos con la URL de la factura"
                    );

                    return;
                  }
                  window.open(`${DOMAIN}/${bill.invoiceUrl}`);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <BillsFilter
        branchs={branchsOptions}
        currencies={currencyOptions}
        personal={personalOptions}
        visible={filterVisible}
        setVisible={setFilterVisible}
        formik={filterFormik}
        subcategories={subcategoriesOptions}
      />

      <Formik
        enableReinitialize
        initialValues={currentBillEdit ?? billInitialValues}
        validationSchema={billValidationSchema}
        onSubmit={(values, formikHelpers) => {
          if (currentBillEdit) {
            updateBill({
              ...values,
              time:
                values.time && values.time.split(":").length == 3
                  ? values.time
                  : values.time
                    ? values.time + ":00"
                    : "00:00:00",
              branchId: id,
              id: currentBillEdit.id,
            });
          } else {
            values.time = values.time ? values.time + ":00" : "00:00:00";
            createBill({ ...values, branchId: id });
          }
          formikHelpers.resetForm();
          setBillFormVisible(false);
          setCurrentBillEdit(undefined);
        }}
      >
        {({
          handleChange,
          handleSubmit,
          errors,
          values,
          setFieldValue,
          resetForm,
        }) => (
          <DialogCreate
            OnclickCreate={handleSubmit}
            OnclickCancel={() => {
              resetForm();
              setBillFormVisible(false);
              setCurrentBillEdit(undefined);
            }}
            isLoading={createBillResult.isLoading || updateBillResult.isLoading}
            tittleButtonCreate={
              currentBillEdit ? t("general.edit") : t("general.create")
            }
            tittleButtonCancel={t("general.cancel")}
            open={billFormVisible}
            setOpen={() => setBillFormVisible(false)}
            tittle={t("accounting.views.create_expense")}
            subTittle={t("pages.accounting.createTransactionDescription")}
            onClose={handleClose}
          >
            <form>
              <div className="mt-6 mx-2">
                <div className="space-y-12">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("pages.bills.bill")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("forms.label.date")}
                          span={t("general.mandatory")}
                          name="date"
                          type="date"
                          placeholder={t("forms.label.date")}
                          onChange={handleChange}
                          error={!!errors.date}
                          textError={errors.date}
                          value={values.date}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("forms.label.time")}
                          span={t("general.mandatory")}
                          name="time"
                          type="time"
                          placeholder={t("forms.label.time")}
                          onChange={handleChange}
                          error={!!errors.time}
                          textError={errors.time}
                          value={values.time}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("forms.label.concept")}
                          span={t("general.mandatory")}
                          name="subject"
                          type="text"
                          placeholder={t("forms.label.concept")}
                          onChange={handleChange}
                          error={!!errors.subject}
                          textError={errors.subject}
                          value={values.subject}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("forms.label.description")}
                          span={t("general.optional")}
                          name="description"
                          error={!!errors.description}
                          textError={errors.description}
                          onChange={handleChange}
                          value={values.description}
                          type="text"
                          placeholder={t("forms.label.description")}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputDropDownSpan
                          label={t("forms.label.amount")}
                          span={t("general.mandatory")}
                          name="amount"
                          type="number"
                          placeholder={t("forms.label.amount")}
                          onChange={handleChange}
                          value={values.amount}
                          nameSelect="currency"
                          selectValue={values.currency}
                          onSelectChange={handleChange}
                          options={currencies.map((item) => ({
                            id: item.currency,
                            text: item.currency,
                          }))}
                          error={!!errors.amount}
                          textError={errors.amount || errors.currency}
                          errorSpan={!!errors.currency}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("pages.accounting.provider_name")}
                          span={t("general.mandatory")}
                          name="selledBy"
                          error={!!errors.selledBy}
                          textError={errors.selledBy}
                          onChange={handleChange}
                          value={values.selledBy}
                          type="text"
                          placeholder={t("pages.accounting.provider_name")}
                        />
                      </div>
                      <div className="col-span-full w-full lg:w-auto">
                        <Select
                          name="approvedBy"
                          span={t("general.mandatory")}
                          error={!!errors.approvedBy}
                          textError={errors.approvedBy}
                          selected={personalOptions?.find(
                            (personal) => personal.id == values.approvedBy
                          )}
                          setSelected={(option) =>
                            setFieldValue("approvedBy", option.id)
                          }
                          options={personalOptions}
                          tittle={t("pages.bills.approvedBy")}
                          textDescribe=""
                        />
                      </div>
                      <div className="col-span-full w-full lg:w-auto">
                        <Select
                          name="createdBy"
                          span={t("general.mandatory")}
                          error={!!errors.subcategory}
                          textError={errors.subcategory}
                          selected={subcategoriesOptions?.find(
                            (subcategory) =>
                              subcategory.id == values.subcategory
                          )}
                          setSelected={(option) =>
                            setFieldValue("subcategory", option.id)
                          }
                          options={subcategoriesOptions}
                          tittle={t("pages.bills.subcategories")}
                          textDescribe=""
                        />
                      </div>
                      {currentBillEdit ? (
                        <div className="col-span-full">
                          <p className="text-red-500">
                            Es posible editar el gasto, sin embargo, para
                            modificar los archivos PDF y XML que se enviaron a
                            Hacienda, es necesario hacerlo a través del sitio
                            web de&nbsp;
                            <a
                              href="https://www.facturaelectronica.cr/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary cursor-pointer"
                            >
                              GTI.
                            </a>
                          </p>
                        </div>
                      ) : (
                        <Fragment>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("pages.bills.invoice")}
                              span={t("general.optional")}
                              name="invoice"
                              error={!!errors.invoice}
                              textError={errors.invoice}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const file = event.target.files?.[0];
                                if (file) {
                                  setFieldValue("invoice", file);
                                } else {
                                  toasts.warning(
                                    "Oops!",
                                    "No pudimos cargar el archivo"
                                  );
                                }
                              }}
                              type="file"
                              accept=".pdf"
                              placeholder={t("pages.bills.invoice")}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("pages.accounting.xml")}
                              span={t("general.optional")}
                              name="xml"
                              error={!!errors.xml}
                              textError={errors.xml}
                              onChange={handleChange}
                              value={values.xml}
                              type="file"
                              accept=".xml"
                              placeholder={t("pages.accounting.xml")}
                            />
                          </div>
                        </Fragment>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </DialogCreate>
        )}
      </Formik>
    </Fragment>
  );
};
